import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HistoryIcon from '@mui/icons-material/History';

import { ReactComponent as TemplatesIcon } from '../icons/Templates.svg';
import { ReactComponent as DocumentsIcon } from '../icons/Documents.svg';
import { ReactComponent as PlanOfCareIcon } from '../icons/PlanOfCare.svg';
import { ReactComponent as NotesIcon } from '../icons/Notes.svg';
import { ReactComponent as NotificationRulesIcon } from '../icons/NotificationRules.svg';
import { ReactComponent as DevicesIcon } from '../icons/Devices.svg';
import { ReactComponent as AuditLogIcon } from '../icons/AuditLog.svg';
import { ReactComponent as ReportsIcon } from '../icons/Reports.svg';
import { ReactComponent as UsersIcon } from '../icons/Users.svg';
import { ReactComponent as PatientsIcon } from '../icons/Patients.svg';
import { ReactComponent as InventoryIcon } from '../icons/Inventory.svg';
import { ReactComponent as DashboardIcon } from '../icons/Dashboard.svg';
import { ReactComponent as ActiveUsersIcon } from '../pages/Dashboard/icons/ActiveUsersIcon.svg';
import { ReactComponent as DiagnosisIcon } from '../pages/Dashboard/icons/DiagnosisIcon.svg';
import { ReactComponent as HospitalIcon } from '../icons/Hospital.svg';

import {
  DASHBOARD,
  TRIAGE,
  HOSPITAL,
  PROGRAMS,
  INVENTORY,
  PATIENTS,
  USERS,
  REPORTS,
  ACTIVITY_LOGS,
  PATIENT_DASHBOARD,
  PATIENT_DEVICES,
  PATIENT_NOTIFICATION_RULES,
  PATIENT_NOTES,
  PATIENT_PLAN_OF_CARE,
  PATIENT_REPORTS,
  AUDIT_LOGS,
  TEMPLATES,
  ICD_10_CODES,
  PATIENT_DOCUMENTS,
} from './paths';

import { UserRole, UserPermission } from '~/types';

interface IGeneralMenuItemsProps {
  userRole: UserRole;
  userPermissions: UserPermission[];
}

export const getGeneralMenuItems = ({ userRole, userPermissions }: IGeneralMenuItemsProps) => {
  const permissions = {
    triage:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      userPermissions.includes(UserPermission.ManageTriage),
    programs: [UserRole.ProgramAdmin, UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole),
    hospital:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      [UserPermission.ManagePatients, UserPermission.MonitorPatients].some(permission =>
        userPermissions.includes(permission),
      ),
    inventory:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      userPermissions.includes(UserPermission.ManageDevices),
    patients:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      [UserPermission.ManagePatients, UserPermission.MonitorPatients].some(permission =>
        userPermissions.includes(permission),
      ),
    users:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      [UserPermission.ManageUsers, UserPermission.ManageMobileUsers].some(permission =>
        userPermissions.includes(permission),
      ),
    reports:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      userPermissions.includes(UserPermission.ManageReports),
    activityLogs:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      [UserPermission.ManagePatients, UserPermission.MonitorPatients].some(permission =>
        userPermissions.includes(permission),
      ),
    templates: [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole),
    diagnoses: [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole),
    auditLogs: userRole === UserRole.SystemAdmin,
  };

  return [
    {
      url: DASHBOARD,
      title: 'Dashboard',
      icon: DashboardIcon,
    },
    ...(permissions.triage
      ? [
          {
            url: TRIAGE,
            title: 'Triage',
            icon: ActiveUsersIcon,
          },
        ]
      : []),
    ...(permissions.hospital
      ? [
          {
            url: HOSPITAL,
            title: 'Hospital',
            icon: HospitalIcon,
          },
        ]
      : []),
    ...(permissions.programs
      ? [
          {
            url: PROGRAMS,
            title: 'Programs',
            icon: AccountTreeIcon,
          },
        ]
      : []),
    ...(permissions.inventory
      ? [
          {
            url: INVENTORY,
            title: 'Inventory',
            icon: InventoryIcon,
          },
        ]
      : []),
    ...(permissions.patients
      ? [
          {
            url: PATIENTS,
            title: 'Patients',
            icon: PatientsIcon,
          },
        ]
      : []),
    ...(permissions.users
      ? [
          {
            url: USERS,
            title: 'Users',
            icon: UsersIcon,
          },
        ]
      : []),
    ...(permissions.reports
      ? [
          {
            url: REPORTS,
            title: 'Reports',
            icon: ReportsIcon,
          },
        ]
      : []),
    ...(permissions.activityLogs
      ? [
          {
            url: ACTIVITY_LOGS,
            title: 'Activity Logs',
            icon: HistoryIcon,
          },
        ]
      : []),
    ...(permissions.templates
      ? [
          {
            url: TEMPLATES,
            title: 'Templates',
            icon: TemplatesIcon,
          },
        ]
      : []),
    ...(permissions.diagnoses
      ? [
          {
            url: ICD_10_CODES,
            title: 'Diagnoses',
            icon: DiagnosisIcon,
          },
        ]
      : []),
    ...(permissions.auditLogs
      ? [
          {
            url: AUDIT_LOGS,
            title: 'Audit Logs',
            icon: AuditLogIcon,
          },
        ]
      : []),
  ];
};

const getUrl = (url: string, patientId: string) => url.replace(':id', patientId);

export const getPatientMenuItems = (patientId: string) => [
  {
    url: getUrl(PATIENT_DASHBOARD, patientId),
    title: 'Dashboard',
    icon: DashboardIcon,
  },
  {
    url: getUrl(PATIENT_DEVICES, patientId),
    title: 'Devices',
    icon: DevicesIcon,
  },
  {
    url: getUrl(PATIENT_NOTIFICATION_RULES, patientId),
    title: 'Notification Rules',
    icon: NotificationRulesIcon,
  },
  {
    url: getUrl(PATIENT_NOTES, patientId),
    title: 'Notes',
    icon: NotesIcon,
  },
  {
    url: getUrl(PATIENT_PLAN_OF_CARE, patientId),
    title: 'Plan of Care',
    icon: PlanOfCareIcon,
  },
  {
    url: getUrl(PATIENT_DOCUMENTS, patientId),
    title: 'Documents',
    icon: DocumentsIcon,
  },
  {
    url: getUrl(PATIENT_REPORTS, patientId),
    title: 'Reports',
    icon: ReportsIcon,
  },
];
